import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const events = [
  {
    id: 'weddings',
    title: 'Weddings',
    tagline: 'Your night to remember, forever.',
    desc: 'Turn your reception into an otherworldly experience. Our LED floors create an ethereal, romantic atmosphere that becomes the most photographed moment of the night.',
    image: 'https://images.pexels.com/photos/35985242/pexels-photo-35985242.jpeg?auto=compress&w=1200',
    accent: '#C084FC',
    label: 'WEDDINGS',
  },
  {
    id: 'nightclubs',
    title: 'Nightclubs',
    tagline: 'The floor that becomes the show.',
    desc: 'Elevate your venue from a club to a destination. Reactive floors sync with your DJ\'s music — creating a visual experience that keeps guests talking and coming back.',
    image: 'https://images.unsplash.com/photo-1599596706483-a7b4863f0d7c?w=1200&q=80',
    accent: '#818CF8',
    label: 'NIGHTCLUBS',
  },
  {
    id: 'corporate',
    title: 'Corporate Events',
    tagline: 'Impress. Inspire. Be remembered.',
    desc: 'Stand apart at product launches, galas, and conferences. Luma turns brand activations into fully immersive environments that leave a lasting impression.',
    image: 'https://images.pexels.com/photos/1888883/pexels-photo-1888883.jpeg?auto=compress&w=1200',
    accent: '#38BDF8',
    label: 'CORPORATE',
  },
  {
    id: 'festivals',
    title: 'Festivals',
    tagline: 'The installation everyone stops for.',
    desc: 'In a sea of stages and booths, Luma creates a landmark. An LED installation that draws crowds from across the field and becomes the defining visual of your festival.',
    image: 'https://images.unsplash.com/photo-1750763537578-6e592eabf730?w=1200&q=80',
    accent: '#FB923C',
    label: 'FESTIVALS',
  },
  {
    id: 'art',
    title: 'Art Installations',
    tagline: 'Light as the medium. Space as the canvas.',
    desc: 'For curators and artists who think beyond the wall. Our LED systems become a living, breathing artwork — interactive, programmable, and endlessly expressive.',
    image: 'https://images.pexels.com/photos/7317332/pexels-photo-7317332.jpeg?auto=compress&w=1200',
    accent: '#34D399',
    label: 'ART',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' },
  }),
};

export const EventTypes = () => {
  const [activeEvent, setActiveEvent] = useState(events[0].id);
  const active = events.find((e) => e.id === activeEvent);

  const scrollToBooking = () => {
    const el = document.querySelector('#booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-28 lg:py-36 px-6 overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(139,92,246,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-14 lg:mb-20">
          <motion.p
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-4"
          >
            Event Types
          </motion.p>
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
            className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white"
            data-testid="events-headline"
          >
            Every event.
            <span className="gradient-text"> Elevated.</span>
          </motion.h2>
        </div>

        {/* Main showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left - Event selector tabs */}
          <div className="lg:col-span-4 flex flex-col justify-center gap-2">
            {events.map((event, i) => (
              <motion.button
                key={event.id}
                data-testid={`event-tab-${event.id}`}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i + 1}
                onClick={() => setActiveEvent(event.id)}
                className={`group text-left px-6 py-5 rounded-xl transition-all duration-300 ${
                  activeEvent === event.id
                    ? 'glass-card'
                    : 'hover:bg-white/3'
                }`}
                style={{
                  borderLeft: activeEvent === event.id
                    ? `2px solid ${event.accent}`
                    : '2px solid transparent',
                }}
              >
                <span
                  className="text-xs font-manrope tracking-[0.25em] uppercase mb-1 block transition-colors duration-200"
                  style={{ color: activeEvent === event.id ? event.accent : 'rgba(255,255,255,0.35)' }}
                >
                  {event.label}
                </span>
                <span
                  className="font-outfit font-semibold text-lg leading-tight tracking-tight transition-colors duration-200"
                  style={{ color: activeEvent === event.id ? '#fff' : 'rgba(255,255,255,0.5)' }}
                >
                  {event.title}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Right - Active event showcase */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeEvent}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative rounded-2xl overflow-hidden"
                style={{ minHeight: '480px' }}
                data-testid={`event-showcase-${activeEvent}`}
              >
                {/* Image */}
                <img
                  src={active.image}
                  alt={active.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: 'brightness(0.5)' }}
                />
                {/* Overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to top, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.4) 50%, rgba(10,10,10,0.1) 100%)`,
                  }}
                />
                {/* Accent overlay */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    background: `radial-gradient(ellipse at bottom left, ${active.accent}40 0%, transparent 60%)`,
                  }}
                />

                {/* Content */}
                <div className="relative z-10 p-8 lg:p-12 h-full flex flex-col justify-end" style={{ minHeight: '480px' }}>
                  <span
                    className="text-xs font-manrope tracking-[0.3em] uppercase mb-3 block"
                    style={{ color: active.accent }}
                  >
                    {active.label}
                  </span>
                  <h3 className="font-outfit font-black text-3xl lg:text-4xl text-white mb-4 leading-tight tracking-tight">
                    {active.tagline}
                  </h3>
                  <p className="font-manrope text-sm lg:text-base text-white/60 max-w-xl mb-8 leading-relaxed">
                    {active.desc}
                  </p>
                  <button
                    data-testid={`event-book-btn-${activeEvent}`}
                    onClick={scrollToBooking}
                    className="inline-flex items-center gap-2 font-manrope font-semibold text-sm text-white group/btn"
                  >
                    <span style={{ color: active.accent }}>Book This Experience</span>
                    <ArrowRight
                      size={16}
                      style={{ color: active.accent }}
                      className="transition-transform duration-200 group-hover/btn:translate-x-1"
                    />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventTypes;
